import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const COURSE_URL = "https://fetc.in/api/v1/courses";

export const courseApi = createApi({
  reducerPath: "coursesApi",
  tagTypes: ["Courses", "Progress"],

  baseQuery: fetchBaseQuery({
    baseUrl: COURSE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth?.token;

      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }

      return headers;
    },
  }),

  endpoints: (builder) => ({
    /* ================= COURSES ================= */

    createCourse: builder.mutation({
      query: (formData) => ({
        url: "/create",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Courses"],
    }),

      getCourses: builder.query({
      query: (params) => ({
        url: "/list",
        params,
      }),
      providesTags: ["Courses"],
    }),

    getPublishedCourses: builder.query({
      query: () => "/published",
      providesTags: ["Courses"],
    }),

    getCreatorCourses: builder.query({
      query: () => "/user",
      providesTags: ["Courses"],
    }),

    getCourseBySlug: builder.query({
      query: (slug) => `/${slug}`,
      providesTags: (r, e, slug) => [{ type: "Courses", id: slug }],
    }),

    /* ================= PROGRESS ================= */

    getCourseProgress: builder.query({
      query: (courseId) => `/${courseId}/progress`,
      providesTags: (r, e, courseId) => [{ type: "Progress", id: courseId }],
      transformResponse: (r) => ({
        lessonsProgress: Array.isArray(r.lessonsProgress) ? r.lessonsProgress : [],
        percentage: Number(r.percentage) || 0,
        score: Number(r.score) || 0,
        isCompleted: !!r.isCompleted,
      }),
    }),

    updateLessonCompletion: builder.mutation({
      query: ({ courseId, lessonId, completed, score }) => ({
        url: `/${courseId}/lessons/${lessonId}/completion`,
        method: "PUT",
        body: { completed, score },
      }),
      invalidatesTags: (r, e, { courseId }) => [{ type: "Progress", id: courseId }],
    }),

    /* ================= RESULTS ================= */

    getCourseResult: builder.query({
      query: (courseId) => `/${courseId}/result`,
    }),

    saveCourseResult: builder.mutation({
      query: ({ courseId, result }) => ({
        url: `/${courseId}/result`,
        method: "POST",
        body: result,
      }),
      invalidatesTags: (r, e, { courseId }) => [{ type: "Progress", id: courseId }],
    }),

    /* ================= COURSE CRUD ================= */

    editCourse: builder.mutation({
      query: ({ slug, data }) => ({
        url: `/${slug}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (r, e, { slug }) => [{ type: "Courses", id: slug }],
    }),

    deleteCourse: builder.mutation({
      query: (slug) => ({
        url: `/${slug}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Courses"],
    }),

    /* ================= LESSONS ================= */

    addLesson: builder.mutation({
      query: ({ courseSlug, lessonData }) => ({
        url: `/${courseSlug}/lessons`,
        method: "POST",
        body: lessonData,
      }),
      invalidatesTags: (r, e, { courseSlug }) => [{ type: "Courses", id: courseSlug }],
    }),

    editLesson: builder.mutation({
      query: ({ courseSlug, lessonId, lessonData }) => ({
        url: `/${courseSlug}/lessons/${lessonId}`,
        method: "PUT",
        body: lessonData,
      }),
      invalidatesTags: (r, e, { courseSlug }) => [{ type: "Courses", id: courseSlug }],
    }),

    deleteLesson: builder.mutation({
      query: ({ courseSlug, lessonId }) => ({
        url: `/${courseSlug}/lessons/${lessonId}`,
        method: "DELETE",
      }),
      invalidatesTags: (r, e, { courseSlug }) => [{ type: "Courses", id: courseSlug }],
    }),

    /* ================= UPLOADS ================= */

    uploadCourseFile: builder.mutation({
      query: (formData) => ({
        url: "/upload/file",
        method: "POST",
        body: formData,
      }),
    }),

    uploadCourseAudio: builder.mutation({
      query: (formData) => ({
        url: "/upload/audio",
        method: "POST",
        body: formData,
      }),
    }),

    uploadCourseImage: builder.mutation({
      query: (formData) => ({
        url: "/upload/image",
        method: "POST",
        body: formData,
      }),
    }),

    uploadCourseVideo: builder.mutation({
      query: (formData) => ({
        url: "/upload/video",
        method: "POST",
        body: formData,
      }),
    }),

    /* ================= STUDENT ================= */

    startLessonExercise: builder.mutation({
      query: ({ lessonId }) => ({
        url: `/student/lessons/${lessonId}/start`,
        method: "POST",
      }),
    }),

    submitLessonExercise: builder.mutation({
      query: ({ attemptId, answers }) => ({
        url: `/student/attempts/${attemptId}/submit`,
        method: "POST",
        body: { answers },
      }),
    }),

    getLessonResult: builder.query({
      query: (attemptId) => `/student/attempts/${attemptId}/result`,
    }),
  }),
});

export const {
  useCreateCourseMutation,
  useGetCoursesQuery,
  useGetPublishedCoursesQuery,
  useGetCreatorCoursesQuery,
  useGetCourseBySlugQuery,
  useGetCourseProgressQuery,
  useEditCourseMutation,
  useDeleteCourseMutation,
  useAddLessonMutation,
  useEditLessonMutation,
  useGetCourseResultQuery,
  useUpdateLessonCompletionMutation,
  useSaveCourseResultMutation,
  useUploadCourseFileMutation,
  useUploadCourseImageMutation,
  useUploadCourseVideoMutation,
  useUploadCourseAudioMutation,
  useDeleteLessonMutation,
} = courseApi;